// Programmatically renders a shareable "movie poster" for a Greenlight result:
// headshot collage, big title, "DIRECTED BY" credits, mini-print block, score.
// TMDB images are CORS-open (access-control-allow-origin: *), so the canvas
// stays untainted and can be exported to PNG.

const W = 1000;
const H = 1500;

const TIER_COLORS = {
  S: "#facc15",
  A: "#22c55e",
  B: "#38bdf8",
  C: "#9ca3af",
  D: "#fb923c",
  F: "#ef4444",
};

function loadImage(src) {
  return new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    // The draft UI loads these same URLs without crossorigin, so the browser
    // cache may hold CORS-less copies that taint-block here. Retry with a
    // cache-busting param to force a fresh CORS-enabled fetch.
    img.onerror = () => {
      const retry = new Image();
      retry.crossOrigin = "anonymous";
      retry.onload = () => resolve(retry);
      retry.onerror = () => resolve(null);
      retry.src = src + (src.includes("?") ? "&" : "?") + "glcors=1";
    };
    img.src = src;
  });
}

function circleImage(ctx, img, x, y, r, name) {
  ctx.save();
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();
  if (img) {
    // cover-fit
    const scale = Math.max((r * 2) / img.width, (r * 2) / img.height);
    const w = img.width * scale;
    const h = img.height * scale;
    ctx.drawImage(img, x - w / 2, y - h / 2, w, h);
  } else {
    ctx.fillStyle = "#26283a";
    ctx.fillRect(x - r, y - r, r * 2, r * 2);
    ctx.fillStyle = "#8b8da0";
    ctx.font = `700 ${r * 0.8}px Georgia, serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const initials = (name || "?")
      .split(" ")
      .map((p) => p[0])
      .slice(0, 2)
      .join("");
    ctx.fillText(initials, x, y + r * 0.05);
  }
  ctx.restore();
  // gold ring
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.strokeStyle = "rgba(250, 204, 21, 0.6)";
  ctx.lineWidth = 4;
  ctx.stroke();
}

function fitText(ctx, text, maxWidth, basePx, font, minPx = 24) {
  let px = basePx;
  do {
    ctx.font = `${font.replace("{px}", px)}`;
    if (ctx.measureText(text).width <= maxWidth) break;
    px -= 1;
  } while (px > minPx);
  return px;
}

export async function renderPosterCanvas(result, images = {}, playerName) {
  const m = result.movie;
  const r = result.results;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // ---- background ----
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, "#1a1a2e");
  bg.addColorStop(0.55, "#101020");
  bg.addColorStop(1, "#08080f");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);
  // vignette
  const vg = ctx.createRadialGradient(W / 2, H * 0.38, 150, W / 2, H * 0.38, 900);
  vg.addColorStop(0, "rgba(250,204,21,0.07)");
  vg.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = vg;
  ctx.fillRect(0, 0, W, H);
  // border frame
  ctx.strokeStyle = "rgba(250,204,21,0.35)";
  ctx.lineWidth = 6;
  ctx.strokeRect(28, 28, W - 56, H - 56);

  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";

  // ---- production banner ----
  ctx.fillStyle = "#c9cbd8";
  ctx.font = "600 26px Georgia, serif";
  ctx.fillText(
    `${(playerName || "A GREENLIGHT").toUpperCase()}${playerName ? "'S" : ""} PRODUCTION`,
    W / 2,
    105
  );

  // ---- top billing (cast names) ----
  const cast = [
    m.lead?.name,
    m.supporting_actor_1?.name,
    m.supporting_actor_2?.name,
    m.supporting_actress?.name,
  ].filter(Boolean);
  ctx.fillStyle = "#facc15";
  ctx.font = "700 30px Georgia, serif";
  ctx.fillText(cast.slice(0, 2).join("   ·   ").toUpperCase(), W / 2, 170);
  ctx.fillText(cast.slice(2).join("   ·   ").toUpperCase(), W / 2, 212);

  // ---- headshot collage ----
  const people = [
    { key: "lead", r: 150, x: W / 2, y: 470 },
    { key: "supporting_actor_1", r: 105, x: W / 2 - 270, y: 430 },
    { key: "supporting_actor_2", r: 105, x: W / 2 + 270, y: 430 },
    { key: "supporting_actress", r: 95, x: W / 2 - 190, y: 625 },
    { key: "director", r: 95, x: W / 2 + 190, y: 625 },
  ];
  const imgs = await Promise.all(people.map((p) => loadImage(images[p.key])));
  // draw side circles first, lead last (on top)
  [1, 2, 3, 4, 0].forEach((idx) => {
    const p = people[idx];
    circleImage(ctx, imgs[idx], p.x, p.y, p.r, m[p.key]?.name);
  });

  // ---- title (archetype) ----
  const title = (r.archetype || "UNTITLED").toUpperCase();
  const titlePx = fitText(ctx, title, W - 140, 92, "900 {px}px Georgia, serif");
  ctx.fillStyle = "#ffffff";
  ctx.font = `900 ${titlePx}px Georgia, serif`;
  ctx.shadowColor = "rgba(250,204,21,0.45)";
  ctx.shadowBlur = 28;
  ctx.fillText(title, W / 2, 870);
  ctx.shadowBlur = 0;

  // tagline
  ctx.fillStyle = "#c9cbd8";
  ctx.font = "italic 600 34px Georgia, serif";
  ctx.fillText(
    `A ${m.genre?.name || "Mystery"} filmed in ${m.location?.name || "Hollywood"}`,
    W / 2,
    930
  );

  // directed by
  ctx.fillStyle = "#facc15";
  ctx.font = "700 38px Georgia, serif";
  ctx.fillText(
    `DIRECTED BY ${(m.director?.name || "UNKNOWN").toUpperCase()}`,
    W / 2,
    1000
  );

  // ---- stats row ----
  const stats = [
    [`$${r.worldwideGross >= 1000 ? (r.worldwideGross / 1000).toFixed(2) + "B" : Math.round(r.worldwideGross) + "M"}`, "WORLDWIDE"],
    [`${r.oscarWins}`, "OSCAR WINS"],
    [`${r.criticScore}`, "CRITICS"],
    [`${r.movieScore}`, "SCORE"],
  ];
  const sw = (W - 160) / stats.length;
  stats.forEach(([val, lbl], i) => {
    const cx = 80 + sw * i + sw / 2;
    ctx.fillStyle = "#ffffff";
    ctx.font = "800 52px Helvetica, Arial, sans-serif";
    ctx.fillText(val, cx, 1110);
    ctx.fillStyle = "#8b8da0";
    ctx.font = "600 22px Helvetica, Arial, sans-serif";
    ctx.fillText(lbl, cx, 1145);
  });

  // ---- tier badge ----
  if (r.tier) {
    const bx = W / 2;
    const by = 1240;
    ctx.fillStyle = TIER_COLORS[r.tier] || "#9ca3af";
    const bs = 96;
    const rad = 22;
    ctx.beginPath();
    ctx.roundRect(bx - bs / 2, by - bs / 2, bs, bs, rad);
    ctx.fill();
    ctx.fillStyle = r.tier === "S" ? "#111" : "#fff";
    ctx.font = "900 64px Helvetica, Arial, sans-serif";
    ctx.textBaseline = "middle";
    ctx.fillText(r.tier, bx, by + 6);
    ctx.textBaseline = "alphabetic";
  }

  // ---- mini-print credits block ----
  ctx.fillStyle = "#6f7186";
  const credits = `A ${m.genre?.name || ""} PICTURE  ·  BUDGET ${m.budget?.name || ""}  ·  STARRING ${cast.join(", ").toUpperCase()}`;
  const creditsPx = fitText(
    ctx,
    credits,
    W - 120,
    19,
    "500 {px}px Helvetica, Arial, sans-serif",
    11
  );
  ctx.font = `500 ${creditsPx}px Helvetica, Arial, sans-serif`;
  ctx.fillText(credits, W / 2, 1330);
  if (r.bestPicture?.nominated) {
    ctx.fillStyle = "#facc15";
    ctx.font = "600 24px Georgia, serif";
    ctx.fillText(
      r.bestPicture.won
        ? "🏆 ACADEMY AWARD WINNER — BEST PICTURE"
        : `🏆 BEST PICTURE NOMINEE${r.bestPicture.lostTo ? ` — LOST TO ${r.bestPicture.lostTo.toUpperCase()}` : ""}`,
      W / 2,
      1370
    );
  }

  // ---- footer ----
  ctx.fillStyle = "#ffffff";
  ctx.font = "800 30px Helvetica, Arial, sans-serif";
  ctx.fillText(`🎬 GREENLIGHT  ·  ${result.date || ""}`, W / 2, 1425);
  ctx.fillStyle = "#8b8da0";
  ctx.font = "600 24px Helvetica, Arial, sans-serif";
  ctx.fillText("crossover.games", W / 2, 1460);

  return canvas;
}

export async function posterBlobUrl(result, images, playerName) {
  const canvas = await renderPosterCanvas(result, images, playerName);
  return new Promise((resolve) =>
    canvas.toBlob((b) => resolve({ url: URL.createObjectURL(b), blob: b }), "image/png")
  );
}
